// src/components/teams/TeamRow.tsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { TeamModel, getTeamDisplayName, getTeamPagePath } from "../../models/TeamModel";
import { useTranslation } from "react-i18next";
import {
  FaFutbol, FaBasketballBall, FaVolleyballBall,
  FaFootballBall, FaBaseballBall, FaHockeyPuck,
  FaLock,
} from "react-icons/fa";
import { MdSportsHandball } from "react-icons/md";
import "./TeamRow.css";

interface TeamRowProps {
  team: TeamModel;
  isSelected: boolean;
  onSelect: () => void;
  // Called on mobile after navigation so App.tsx can switch to the entries pane
  onMobileNavigate?: () => void;
}

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";

const resolveLogoUrl = (url: string | null): string | null => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
};

// Flags are stored under flags/ path — they need cover to fill the circle;
// club logos need contain to avoid cropping
const isFlag = (url: string | null): boolean => {
  return !!url && url.includes("flags/");
};

const SportIcon: React.FC<{ sportType: string }> = ({ sportType }) => {
  const p = { size: 11 };
  switch (sportType) {
    case "FOOTBALL":          return <FaFutbol {...p} />;
    case "BASKETBALL":        return <FaBasketballBall {...p} />;
    case "VOLLEYBALL":        return <FaVolleyballBall {...p} />;
    case "AMERICAN_FOOTBALL": return <FaFootballBall {...p} />;
    case "BASEBALL":          return <FaBaseballBall {...p} />;
    case "ICE_HOCKEY":        return <FaHockeyPuck {...p} />;
    case "HANDBALL":          return <MdSportsHandball {...p} />;
    default:                  return <FaFutbol {...p} />;
  }
};

export const TeamRow: React.FC<TeamRowProps> = ({ team, isSelected, onSelect, onMobileNavigate }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const displayName = getTeamDisplayName(team);
  const logoSrc = resolveLogoUrl(team.clubLogoUrl);
  const subtitle = [
    team.displayName ? team.clubShortName : null,
    team.gender === "FEMALE" ? "Kadın" : team.gender === "MIXED" ? "Karma" : null,
    team.teamCode ? team.teamCode.toUpperCase() : null,
  ].filter(Boolean).join(" · ");

  const recentCount = team.recentEntryCount ?? 0;

  const getHeatClass = (count: number): string => {
    if (count === 0)  return "heat-0";
    if (count < 3)    return "heat-1";
    if (count < 8)    return "heat-2";
    if (count < 20)   return "heat-3";
    if (count < 50)   return "heat-4";
    return "heat-5";
  };

  const formatCount = (count: number): string => {
    if (count >= 1_000_000) return (count / 1_000_000).toFixed(1).replace(".", ",") + "m";
    if (count >= 1_000)     return (count / 1_000).toFixed(1).replace(".", ",") + "b";
    return String(count);
  };

  const handleClick = () => {
    onSelect();
    navigate(getTeamPagePath(team));
    // On mobile: signal App.tsx to switch from the list pane to the content pane
    onMobileNavigate?.();
  };

  return (
    <div
      className={`team-row ${isSelected ? "selected" : ""} ${team.isLocked ? "locked" : ""}`}
      onClick={handleClick}
    >
      {/* Avatar */}
      <div className="team-row__avatar">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={team.clubShortName}
            className={isFlag(team.clubLogoUrl) ? "flag-img" : "logo-img"}
            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          team.clubShortName.slice(0, 2).toUpperCase()
        )}
      </div>

      {/* Name + subtitle */}
      <div className="team-row__info">
        <div className="team-row__name">{displayName}</div>
        {subtitle && <div className="team-row__subtitle">{subtitle}</div>}
      </div>

      {/* Right side: entry badge + sport icon + lock icon (always rightmost) */}
      <div className="team-row__right">
        <span
          className={`team-row__entry-badge ${getHeatClass(recentCount)}`}
          title={t("team_row_recent_entries", "Son 24 saatte entry")}
        >
          {formatCount(recentCount)}
        </span>
        <span className="team-row__icon">
          <SportIcon sportType={team.sportType} />
        </span>
        {team.isLocked && (
          <span className="row-lock-icon" title={t("topic_locked_tooltip")}>
            <FaLock size={10} />
          </span>
        )}
      </div>
    </div>
  );
};